import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  TextField,
  Autocomplete,
  Chip,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  MenuItem
} from '@mui/material';
import {
  Restaurant,
  ContentCopy,
  Delete,
  Edit as EditIcon,
  Save,
  Print,
  ArrowBack
} from '@mui/icons-material';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { ipdAPI } from '../../services/api';
import PrintHeader from '../shared/PrintHeader';

const MEAL_TIMES = ['7AM', 'Breakfast', '11AM', 'Lunch', '5PM', 'Dinner'];

function DietPlanManagement({ embedded = false, apiOverride, filterFrom, filterTo }) {
  const activeAPI = apiOverride || ipdAPI;
  const { admissionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const patientInfo = location.state || {};

  const [dietItems, setDietItems] = useState([]);
  const [allPlans, setAllPlans] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dateRange, setDateRange] = useState([]);

  // Edit state for specific cell
  const [editingCell, setEditingCell] = useState(null); // { date: 'YYYY-MM-DD', mealTime: '7AM' }
  const [selectedItems, setSelectedItems] = useState([]);
  const [mealNotes, setMealNotes] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Copy dialogs
  const [copyDayDialogOpen, setCopyDayDialogOpen] = useState(false);
  const [copyMealDialogOpen, setCopyMealDialogOpen] = useState(false);
  const [copySourceDate, setCopySourceDate] = useState('');
  const [copySourceMeal, setCopySourceMeal] = useState('');
  const [copyTargetDates, setCopyTargetDates] = useState([]);

  useEffect(() => {
    generateDateRange();
    loadDietItems();
  }, [admissionId]);

  useEffect(() => {
    if (dateRange.length > 0) {
      loadAllDietPlans();
    }
  }, [dateRange]);

  const generateDateRange = () => {
    if (!patientInfo.admissionDate || !patientInfo.plannedDays) {
      setError('Missing admission information. Please go back and try again.');
      return;
    }

    const admissionDate = new Date(patientInfo.admissionDate);
    const dates = [];

    for (let i = 0; i < patientInfo.plannedDays; i++) {
      const date = new Date(admissionDate);
      date.setDate(date.getDate() + i);
      dates.push(formatDate(date));
    }

    setDateRange(dates);
  };

  const loadDietItems = async () => {
    try {
      const response = await activeAPI.getDietItems();
      const data = response.data.data || response.data;
      setDietItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Load diet items error:', err);
    }
  };

  const loadAllDietPlans = async () => {
    setLoading(true);
    try {
      // Fetch all plans for the date range
      const plansData = {};

      for (const dateStr of dateRange) {
        const response = await activeAPI.getDietPlanByDate(admissionId, dateStr);
        const data = response.data.data || response.data;

        if (Array.isArray(data)) {
          data.forEach(plan => {
            const key = `${dateStr}_${plan.mealTime}`;
            plansData[key] = plan;
          });
        }
      }

      setAllPlans(plansData);
    } catch (err) {
      console.error('Load diet plans error:', err);
      setError('Failed to load diet plans');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: 'short'
    });
  };

  const handleEditCell = (dateStr, mealTime) => {
    const key = `${dateStr}_${mealTime}`;
    const plan = allPlans[key];

    setEditingCell({ date: dateStr, mealTime });

    if (plan && plan.items) {
      const items = plan.items.map(item => ({
        ...dietItems.find(di => di.id === item.dietItemId),
        quantity: item.quantity,
        specialInstructions: item.specialInstructions
      }));
      setSelectedItems(items);
      setMealNotes(plan.notes || '');
    } else {
      setSelectedItems([]);
      setMealNotes('');
    }

    setEditDialogOpen(true);
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setSelectedItems([]);
    setMealNotes('');
    setEditDialogOpen(false);
  };

  const handleSaveMeal = async () => {
    if (selectedItems.length === 0) {
      setError('Please select at least one diet item');
      return;
    }

    if (!editingCell) return;

    setSaving(true);
    setError('');

    try {
      const dietItemsData = selectedItems.map(item => ({
        id: item.id,
        quantity: item.quantity || '',
        specialInstructions: item.specialInstructions || ''
      }));

      await activeAPI.createDietPlan(admissionId, {
        planDate: editingCell.date,
        mealTime: editingCell.mealTime,
        dietItems: dietItemsData,
        notes: mealNotes
      });

      setSuccess('Meal plan saved successfully');
      handleCancelEdit();
      loadAllDietPlans();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save meal plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMeal = async (dateStr, mealTime) => {
    if (!window.confirm(`Are you sure you want to delete this meal plan?`)) {
      return;
    }

    const key = `${dateStr}_${mealTime}`;
    const plan = allPlans[key];
    if (!plan) return;

    try {
      await activeAPI.deleteDietPlan(plan.id);
      setSuccess('Meal plan deleted successfully');
      loadAllDietPlans();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete meal plan');
    }
  };

  const handleCopyRow = (sourceDateStr) => {
    setCopySourceDate(sourceDateStr);
    setCopyTargetDates([]);
    setCopyDayDialogOpen(true);
  };

  const handleCopyMeal = (dateStr, mealTime) => {
    setCopySourceDate(dateStr);
    setCopySourceMeal(mealTime);
    setCopyTargetDates([]);
    setCopyMealDialogOpen(true);
  };

  const handleConfirmCopyDay = async () => {
    if (copyTargetDates.length === 0) {
      setError('Please select at least one target date');
      return;
    }

    try {
      // Copy to multiple dates
      for (const targetDate of copyTargetDates) {
        await activeAPI.copyDietDay({
          admissionId: parseInt(admissionId),
          sourceDate: copySourceDate,
          targetDate: targetDate
        });
      }
      setSuccess(`Entire day plan copied to ${copyTargetDates.length} date(s) successfully`);
      setCopyDayDialogOpen(false);
      loadAllDietPlans();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to copy plan');
    }
  };

  const handleConfirmCopyMeal = async () => {
    if (copyTargetDates.length === 0) {
      setError('Please select at least one target date');
      return;
    }

    try {
      // Copy to multiple dates
      for (const targetDate of copyTargetDates) {
        await activeAPI.copyDietMeal({
          admissionId: parseInt(admissionId),
          sourceDate: copySourceDate,
          targetDate: targetDate,
          mealTime: copySourceMeal
        });
      }
      setSuccess(`${copySourceMeal} meal copied to ${copyTargetDates.length} date(s) successfully`);
      setCopyMealDialogOpen(false);
      loadAllDietPlans();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to copy meal');
    }
  };

  const updateItemQuantity = (itemId, quantity) => {
    setSelectedItems(items =>
      items.map(item =>
        item.id === itemId ? { ...item, quantity } : item
      )
    );
  };

  const updateItemInstructions = (itemId, instructions) => {
    setSelectedItems(items =>
      items.map(item =>
        item.id === itemId ? { ...item, specialInstructions: instructions } : item
      )
    );
  };

  const getCellPlan = (dateStr, mealTime) => {
    const key = `${dateStr}_${mealTime}`;
    return allPlans[key];
  };

  return (
    <Box>
      {!embedded && (
        <PrintHeader
          title="Diet Plan"
          patientInfo={patientInfo}
        />
      )}
      <Card elevation={2}>
        <CardContent sx={{ p: 3 }}>
          {!embedded && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }} className="no-print">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={() => navigate('/doctor', { state: { activeTab: 3 } })} sx={{ mr: 2 }}>
                <ArrowBack />
              </IconButton>
              <Restaurant sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
              <Box>
                <Typography variant="h5" component="h2" fontWeight="bold">
                  Diet Plan Management - Tabular View
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Patient: {patientInfo.patientName} | Room: {patientInfo.roomNumber}
                </Typography>
                {patientInfo.plannedDays > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Admission: {new Date(patientInfo.admissionDate).toLocaleDateString('en-GB')} |
                    Duration: {patientInfo.plannedDays} days
                  </Typography>
                )}
              </Box>
            </Box>
            <Button
              variant="outlined"
              startIcon={<Print />}
              onClick={() => window.print()}
            >
              Print
            </Button>
          </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Snackbar
            open={!!success}
            autoHideDuration={3000}
            onClose={() => setSuccess('')}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert severity="success" onClose={() => setSuccess('')}>
              {success}
            </Alert>
          </Snackbar>

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : dateRange.length === 0 ? (
            <Alert severity="warning">
              No date range available. Please ensure admission date and planned days are set.
            </Alert>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120, bgcolor: 'primary.main', color: 'white' }}>
                      Date
                    </TableCell>
                    {MEAL_TIMES.map((mealTime) => (
                      <TableCell
                        key={mealTime}
                        align="center"
                        sx={{ fontWeight: 'bold', minWidth: 180, bgcolor: 'primary.main', color: 'white' }}
                      >
                        {mealTime}
                      </TableCell>
                    ))}
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}
                      className="no-print"
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {((filterFrom || filterTo) ? dateRange.filter(d => (!filterFrom || d >= filterFrom) && (!filterTo || d <= filterTo)) : dateRange).map((dateStr) => (
                    <TableRow key={dateStr} hover>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}>
                        {formatDisplayDate(dateStr)}
                      </TableCell>
                      {MEAL_TIMES.map((mealTime) => {
                        const plan = getCellPlan(dateStr, mealTime);
                        return (
                          <TableCell
                            key={`${dateStr}_${mealTime}`}
                            sx={{
                              cursor: 'pointer',
                              bgcolor: plan ? 'success.50' : 'white',
                              '&:hover': { bgcolor: plan ? 'success.100' : 'grey.50' },
                              position: 'relative',
                              verticalAlign: 'top',
                              p: 1
                            }}
                          >
                            {plan ? (
                              <Box>
                                <Box sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                                  {plan.items.map((item, idx) => (
                                    <Box key={idx} sx={{ mb: 0.5 }}>
                                      <Typography variant="caption" fontWeight="bold">
                                        • {item.name}
                                      </Typography>
                                      {item.quantity && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 1 }}>
                                          {item.quantity}
                                        </Typography>
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                                {plan.notes && (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.7rem' }}>
                                    {plan.notes}
                                  </Typography>
                                )}
                                <Box className="no-print" sx={{ mt: 1, display: 'flex', gap: 0.5 }}>
                                  <Tooltip title="Edit">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditCell(dateStr, mealTime)}
                                      sx={{ p: 0.5 }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Copy to another day">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleCopyMeal(dateStr, mealTime)}
                                      sx={{ p: 0.5 }}
                                    >
                                      <ContentCopy fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteMeal(dateStr, mealTime)}
                                      sx={{ p: 0.5 }}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            ) : (
                              <Box
                                className="no-print"
                                onClick={() => handleEditCell(dateStr, mealTime)}
                                sx={{
                                  textAlign: 'center',
                                  color: 'text.secondary',
                                  py: 2,
                                  fontSize: '0.75rem'
                                }}
                              >
                                Click to add
                              </Box>
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell align="center" className="no-print">
                        <Tooltip title="Copy entire day">
                          <IconButton
                            size="small"
                            onClick={() => handleCopyRow(dateStr)}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCancelEdit} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCell && `Edit Meal - ${formatDisplayDate(editingCell.date)} at ${editingCell.mealTime}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Autocomplete
              multiple
              options={dietItems}
              getOptionLabel={(option) => `${option.name} (${option.category})`}
              value={selectedItems}
              onChange={(_, newValue) => setSelectedItems(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Diet Items"
                  placeholder="Choose items..."
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.id}
                    label={option.name}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
              sx={{ mb: 3 }}
            />

            {selectedItems.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Item Details:
                </Typography>
                {selectedItems.map((item) => (
                  <Paper key={item.id} sx={{ mb: 2, p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      {item.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Quantity"
                        value={item.quantity || ''}
                        onChange={(e) => updateItemQuantity(item.id, e.target.value)}
                        placeholder="e.g., 1 bowl, 2 pieces"
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Special Instructions"
                        value={item.specialInstructions || ''}
                        onChange={(e) => updateItemInstructions(item.id, e.target.value)}
                        placeholder="Any special notes"
                      />
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Meal Notes"
              value={mealNotes}
              onChange={(e) => setMealNotes(e.target.value)}
              placeholder="Any additional notes for this meal..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveMeal}
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
            disabled={saving || selectedItems.length === 0}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Day Dialog */}
      <Dialog open={copyDayDialogOpen} onClose={() => setCopyDayDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Copy Entire Day Plan to Multiple Dates</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Copy from: <strong>{formatDisplayDate(copySourceDate)}</strong>
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 1 }}>
              <Button size="small" variant="outlined" onClick={() => setCopyTargetDates(dateRange.filter(d => d !== copySourceDate))}>
                Select All
              </Button>
              <Button size="small" variant="outlined" onClick={() => setCopyTargetDates([])}>
                Clear All
              </Button>
            </Box>
            <Box sx={{ maxHeight: 260, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
              <Grid container spacing={0}>
                {dateRange.filter(date => date !== copySourceDate).map(date => (
                  <Grid item xs={6} sm={4} key={date}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={copyTargetDates.includes(date)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCopyTargetDates(prev => [...prev, date]);
                            } else {
                              setCopyTargetDates(prev => prev.filter(d => d !== date));
                            }
                          }}
                          size="small"
                        />
                      }
                      label={<Typography variant="caption">{formatDisplayDate(date)}</Typography>}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              This will overwrite any existing plans for the selected dates.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDayDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmCopyDay}
            variant="contained"
            disabled={copyTargetDates.length === 0}
            startIcon={<ContentCopy />}
          >
            Copy to {copyTargetDates.length} Date{copyTargetDates.length !== 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Meal Dialog */}
      <Dialog open={copyMealDialogOpen} onClose={() => setCopyMealDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Copy {copySourceMeal} Meal to Multiple Dates</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Copy from: <strong>{formatDisplayDate(copySourceDate)} - {copySourceMeal}</strong>
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 1 }}>
              <Button size="small" variant="outlined" onClick={() => setCopyTargetDates(dateRange.filter(d => d !== copySourceDate))}>
                Select All
              </Button>
              <Button size="small" variant="outlined" onClick={() => setCopyTargetDates([])}>
                Clear All
              </Button>
            </Box>
            <Box sx={{ maxHeight: 260, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
              <Grid container spacing={0}>
                {dateRange.filter(date => date !== copySourceDate).map(date => (
                  <Grid item xs={6} sm={4} key={date}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={copyTargetDates.includes(date)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCopyTargetDates(prev => [...prev, date]);
                            } else {
                              setCopyTargetDates(prev => prev.filter(d => d !== date));
                            }
                          }}
                          size="small"
                        />
                      }
                      label={<Typography variant="caption">{formatDisplayDate(date)}</Typography>}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              This will overwrite any existing {copySourceMeal} meal plans for the selected dates.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyMealDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmCopyMeal}
            variant="contained"
            disabled={copyTargetDates.length === 0}
            startIcon={<ContentCopy />}
          >
            Copy to {copyTargetDates.length} Date{copyTargetDates.length !== 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DietPlanManagement;
